/**
 * TEN-01: Modal de Aprovisionamiento de Tenant
 * Extraído de TenantsAdminPage para reutilizar en PlatformAdminPage.
 */
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useProvisionTenant } from '@/modules/tenants/hooks/useTenants'
import { Button } from '@/components/Button'
import { Modal } from '@/components/ds/Modal'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { COUNTRY_NAMES } from '@/types'

// ─── Schema de aprovisionamiento ─────────────────────────────────────────────
const provisionSchema = z.object({
  name: z.string().min(2, 'Falta la razón social'),
  schema_name: z
    .string()
    .min(3, 'Mínimo 3 caracteres')
    .max(63, 'Máximo 63 caracteres')
    .regex(/^[a-z][a-z0-9_]{2,62}$/, 'Solo minúsculas, números y guiones bajos. Debe iniciar con letra.'),
  country: z.enum(['CL', 'PE', 'AR', 'MX', 'EC', 'OT']),
  tax_id: z.string().optional(),
  admin_email: z.string().email('Email inválido'),
  admin_first_name: z.string().optional(),
  admin_last_name: z.string().optional(),
  account_type: z.enum(['production', 'trial', 'demo', 'nfr']),
  trial_days: z.coerce.number().int().min(1, 'Mínimo 1 día').max(365, 'Máximo 365 días').optional(),
}).superRefine((data, ctx) => {
  // Demo y trial tienen plazo por defecto; el NFR no, porque caduca cuando
  // termina el contrato de partner y cada contrato dura lo suyo. El backend lo
  // rechaza igual — esto solo evita el viaje para mostrar el mismo error.
  if (data.account_type === 'nfr' && !data.trial_days) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['trial_days'],
      message: 'Indicar la vigencia del contrato de partner.',
    })
  }
})
type ProvisionFormData = z.infer<typeof provisionSchema>

/** Vigencia por defecto de cada tipo de cuenta temporal, en días calendario. */
const DIAS_POR_DEFECTO: Record<string, number> = { demo: 7, trial: 30 }

export function ProvisionTenantModal({ onClose }: { onClose: () => void }) {
  const { mutate: provision, isPending, error } = useProvisionTenant()

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ProvisionFormData>({
    resolver: zodResolver(provisionSchema),
    defaultValues: { country: 'CL', account_type: 'production' },
  })

  const accountType = watch('account_type')
  // Todo lo que no es producción caduca (espejo de `TIPOS_CON_VENCIMIENTO` en el
  // modelo). El NFR entra: no caduca por un contador, pero sí cuando termina el
  // contrato, y esa fecha vive en el mismo campo.
  const llevaPlazo = accountType !== 'production'
  const esNfr = accountType === 'nfr'

  // Auto-generar schema_name desde el nombre de empresa
  const nameValue = watch('name')
  const handleNameBlur = () => {
    if (nameValue) {
      const schema = nameValue
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // quitar tildes
        .replace(/[^a-z0-9\s]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 40)
      setValue('schema_name', schema || '', { shouldValidate: false })
    }
  }

  const onSubmit = (data: ProvisionFormData) => {
    // Una cuenta de producción no lleva plazo. El backend lo limpia igual, pero no
    // hay que mandar un dato que no aplica.
    const payload = llevaPlazo
      ? { ...data, trial_days: data.trial_days || DIAS_POR_DEFECTO[data.account_type] }
      : { ...data, trial_days: undefined }
    provision(payload, { onSuccess: onClose })
  }

  const errorMessage = error
    ? (error as any)?.response?.data?.error?.message ?? 'Error al aprovisionar el tenant.'
    : null

  /*
    El `onBlur` propio se COMPONE con el de `register` en vez de reemplazarlo:
    react-hook-form usa el suyo para marcar el campo como tocado y disparar la
    validación, así que pisarlo la apaga sin que nada lo avise.
  */
  const campoNombre = register('name')

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Aprovisionar Nuevo Tenant"
      subtitle="Se creará un schema de base de datos y se enviará email al administrador"
      actions={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            type="submit"
            form="form-aprovisionar"
            isPending={isPending}
            pendingLabel="Aprovisionando..."
          >
            Aprovisionar
          </Button>
        </>
      }
    >
      <form id="form-aprovisionar" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        {errorMessage && (
          <div className="p-3 bg-danger-surface border border-danger-border rounded-lg text-danger-content text-sm">
            {errorMessage}
          </div>
        )}

        <TextField
          dense
          label="Razón Social"
          required
          placeholder="Empresa XYZ S.A."
          inputProps={{
            ...campoNombre,
            onBlur: e => { campoNombre.onBlur(e); handleNameBlur() },
          }}
          error={errors.name?.message}
        />

        <div className="grid grid-cols-2 gap-3">
          <TextField
            dense
            label="Identificador (Schema)"
            required
            placeholder="empresa_xyz"
            inputProps={{ ...register('schema_name'), style: { fontFamily: 'ui-monospace, monospace' } }}
            error={errors.schema_name?.message}
          />
          <SelectField dense label="País" required selectProps={register('country')}>
            {Object.entries(COUNTRY_NAMES).map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </SelectField>
        </div>

        <TextField
          dense
          label="RUT / NIT / RFC"
          placeholder="76.123.456-7"
          inputProps={register('tax_id')}
        />

        {/* Tipo de cuenta y vigencia. Antes el modal creaba siempre en
            producción: una demo había que armarla por API o por shell. */}
        <div className="grid grid-cols-2 gap-3">
          <SelectField dense label="Tipo de cuenta" required selectProps={register('account_type')}>
            <option value="production">Producción</option>
            <option value="trial">Trial</option>
            <option value="demo">Demo</option>
            <option value="nfr">NFR (Partner)</option>
          </SelectField>
          {llevaPlazo && (
            <TextField
              dense
              label="Vigencia (días)"
              required={esNfr}
              placeholder={DIAS_POR_DEFECTO[accountType] ? String(DIAS_POR_DEFECTO[accountType]) : 'Duración del contrato'}
              inputProps={{ ...register('trial_days'), type: 'number', min: 1, max: 365 }}
              error={errors.trial_days?.message}
            />
          )}
        </div>
        {llevaPlazo && !esNfr && (
          <p className="text-xs text-content-subtle -mt-1">
            Al cumplirse el plazo la cuenta <strong>se suspende sola</strong> y nadie puede
            entrar. Por defecto son {DIAS_POR_DEFECTO[accountType]} días calendario; se puede
            extender después desde el listado.
          </p>
        )}
        {esNfr && (
          <p className="text-xs text-content-subtle -mt-1">
            Una cuenta NFR es <strong>productiva</strong>: emite documentos tributarios reales
            con el certificado y los folios del partner, y no se computa como suscripción
            comercial. No tiene plazo por defecto porque caduca cuando termina el contrato —
            indicar su duración en días; después se puede ajustar desde el listado.
          </p>
        )}

        <hr className="border-divider" />

        <p className="text-xs font-semibold text-content-muted uppercase tracking-wider">
          Administrador inicial
        </p>

        <TextField
          dense
          label="Email del Administrador"
          required
          placeholder="admin@empresa.com"
          inputProps={{ ...register('admin_email'), type: 'email' }}
          error={errors.admin_email?.message}
        />

        <div className="grid grid-cols-2 gap-3">
          <TextField dense label="Nombre" placeholder="Ana" inputProps={register('admin_first_name')} />
          <TextField dense label="Apellido" placeholder="Pérez" inputProps={register('admin_last_name')} />
        </div>
      </form>
    </Modal>
  )
}
